import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { IconButton } from '@/design-system';
import { ns, useT } from '@/i18n';
import { useBodyScrollLock } from '@/lib/hooks/useBodyScrollLock';
import { useEscape } from '@/lib/hooks/useEscape';
import { useFocusTrap } from '@/lib/hooks/useFocusTrap';
import styles from './PromoDialog.module.css';

interface Props {
  /** Accessible name (the visible title). */
  label: string;
  onClose: () => void;
  children: ReactNode;
}

/**
 * Small modal card (scrim click, Esc and ✕ close it; focus trapped and restored). It steps aside while
 * another modal dialog is open (e.g. the home page's rating prompt) and comes back when that one closes.
 */
export function PromoDialog({ label, onClose, children }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const t = useT(ns.common);
  const yielding = useOtherDialogOpen(ref);
  useEscape(!yielding, onClose);
  useFocusTrap(ref, !yielding);
  useBodyScrollLock(true);
  return (
    <>
      <button
        type="button"
        className={styles.scrim}
        aria-label={t('close')}
        tabIndex={-1}
        hidden={yielding}
        onClick={onClose}
      />
      <div
        ref={ref}
        className={styles.card}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        tabIndex={-1}
        hidden={yielding}
      >
        {children}
        <IconButton icon="close" label={t('close')} size={42} className={styles.close} onClick={onClose} />
      </div>
    </>
  );
}

/** Another `aria-modal` dialog than `self` is in the page. */
function useOtherDialogOpen(self: RefObject<HTMLElement | null>): boolean {
  const [other, setOther] = useState(false);
  useEffect(() => {
    const check = () =>
      setOther(
        [...document.querySelectorAll('[role="dialog"][aria-modal="true"]')].some((el) => el !== self.current),
      );
    check();
    const observer = new MutationObserver(check);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [self]);
  return other;
}
